/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   SubscriptionNoticePage.tsx
 *     Feature         :   286 - FCM licence enforcement and estate limits (US4, FR-007)
 *     Description     :   Rendered INSTEAD of the app shell when the licence plug is pulled: no
 *                         navigation and no data, only what happened and how to restore it, per plug
 *                         reason (contracts/portal-gating.md "SPA"; revoked wording: owner, 2026-10-07).
 *                         Kit + tokens + lucide-react only (Constitution XLV).
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React from 'react';
import { PauseCircle, LogOut } from 'lucide-react';
import { Card, Heading, Text, Button } from '@/components/ui';
import type { PlugReason } from '@shared/access/licenses';

const NOTICE: Record<PlugReason, { title: string; message: string; restore?: string }> = {
  'status-suspended': {
    title: 'Subscription suspended',
    message: 'Your subscription is suspended — contact DDS to restore it.',
  },
  'status-revoked': {
    title: 'Access on hold',
    message: 'Your access is on hold while we look into something — DDS will contact you.',
  },
  'no-row': {
    title: 'Subscription not active',
    message: 'Your subscription is not active.',
    restore: 'To restore it, accept the terms in the Fabric Control management portal, or contact DDS.',
  },
  'unknown-value': {
    title: 'Subscription not active',
    message: 'Your subscription is not active.',
    restore: 'To restore it, accept the terms in the Fabric Control management portal, or contact DDS.',
  },
};

export interface SubscriptionNoticePageProps {
  reason: PlugReason;
  onSignOut: () => void;
}

export const SubscriptionNoticePage: React.FC<SubscriptionNoticePageProps> = ({ reason, onSignOut }) => {
  const notice = NOTICE[reason] ?? NOTICE['unknown-value'];
  return (
    <main className="flex min-h-screen items-center justify-center bg-[var(--color-bg-page)] px-4">
      <Card padding="lg" className="w-full max-w-lg text-center">
        <PauseCircle aria-hidden className="mx-auto mb-4 h-10 w-10 text-[var(--color-status-warning)]" />
        <Heading as="h1" level="h4">{notice.title}</Heading>
        <Text className="mt-3">{notice.message}</Text>
        {notice.restore && <Text color="muted" className="mt-2">{notice.restore}</Text>}
        <div className="mt-6 flex justify-center">
          <Button variant="secondary" leftIcon={<LogOut className="h-4 w-4" />} onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </Card>
    </main>
  );
};

export default SubscriptionNoticePage;
